<template>
  <slide-out
    dock="right"
    :size="sideOutCurrentSize"
    ref="contractSlideout"
    @resize="OnResize"
    :min-size="sideOutMinSize"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    :visible.sync="slideouts.active"
    v-on:close="onSlideoutClosing">
    <template v-slot:header>
      <h3 v-if="isFetching" class="font-weight-bold pa-1" small>
        <v-progress-circular :size="16" width="2" indeterminate class="mr-1"></v-progress-circular>
        Fetching Contract ...
      </h3>
      <h3 v-else-if="selected.id" class="font-weight-bold pa-1" small>
        <v-icon left>mdi-pencil-box-outline</v-icon> Update Contract
      </h3>
      <h3 v-else class="font-weight-bold pa-1" small>
        <v-icon left>mdi-plus-box-outline</v-icon> Add Contract
      </h3>
      <v-btn @click="closeUpdateSlideout()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div>
      <div class="text-center d-flex flex-column" v-if="isFetching">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading contract data...</p>
      </div>
      <v-form ref="editContractForm" v-else class="pa-4" v-model="valid">
        <v-row no-gutters style="gap: 8px">
          <v-col>
            <v-select
              :items="contractTypes"
              dense
              outlined
              attach
              v-model="selected.typeText"
              :rules="[formRules.required]"
              label="Contract Type *"
              placeholder="Contract Type">
            </v-select>
          </v-col>
          <v-col>
            <v-text-field
              dense
              outlined
              v-model="selected.number"
              :rules="[formRules.required]"
              type="number"
              label="Contract No. *"
              placeholder="Contract No."></v-text-field
          ></v-col>
        </v-row>
        <v-row no-gutters style="gap: 8px">
          <v-col>
            <v-text-field
              dense
              outlined
              attach
              v-model="selected.purchasingOrganizationText"
              label="Purchasing Organization"
              placeholder="Purchasing Organization">
            </v-text-field>
          </v-col>
          <v-col>
            <v-select
              :items="contractors"
              dense
              outlined
              attach
              :readonly="editMode"
              v-model="selected.contractorId"
              item-text="number"
              item-value="id"
              label="Vendor"
              placeholder="Vendor">
            </v-select
          ></v-col>
        </v-row>
        <DatePicker
          v-model="selected.agreementDate"
          :rules="[formRules.required]"
          label="Agreement Date *"></DatePicker>

        <v-row class="my-3" no-gutters style="gap: 8px">
          <v-col>
            <DatePicker
              v-model="selected.validFrom"
              :rules="[formRules.required]"
              label="Valid From *"></DatePicker>
          </v-col>
          <v-col>
            <DatePicker
              :minDate="selected.validFrom"
              v-model="selected.validTo"
              :rules="[formRules.required]"
              label="Valid To *"></DatePicker>
          </v-col>
        </v-row>

        <v-row no-gutters style="gap: 8px">
          <v-col>
            <v-select
              :items="documentCurrencies"
              dense
              outlined
              attach
              v-model="selected.currency"
              label="Document Currency"
              placeholder="Document Currency">
            </v-select
          ></v-col>
          <v-col>
            <v-select
              :items="facilities"
              dense
              outlined
              attach
              :readonly="editMode"
              v-model="selected.facilityId"
              item-text="name"
              item-value="id"
              label="Plant/Location"
              placeholder="Plant/Location">
            </v-select
          ></v-col>
        </v-row>

        <v-text-field
          dense
          outlined
          v-model="selected.description"
          label="Material/Service Description"
          placeholder="Material/Service Description"></v-text-field>

        <v-row no-gutters style="gap: 8px">
          <v-col>
            <v-text-field
              dense
              outlined
              v-model="selected.targetQuantity"
              type="number"
              label="Target Quantity"
              placeholder="Target Quantity"></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              dense
              outlined
              v-model="selected.netPrice"
              type="number"
              label="Net Price"
              placeholder="Net Price"></v-text-field
          ></v-col>
        </v-row>
        <v-text-field
          dense
          outlined
          v-model="selected.duration"
          :rules="[formRules.required]"
          type="number"
          label="Contract Duration *"
          placeholder="Specify the duration of the contract"></v-text-field>
        <v-row no-gutters style="gap: 8px">
          <v-col>
            <v-select
              :items="paymentTerms"
              dense
              outlined
              attach
              v-model="selected.paymentTermText"
              label="Payment Terms"
              placeholder="Payment Terms">
            </v-select
          ></v-col>
          <v-col>
            <v-select
              :items="contractStatuses"
              dense
              outlined
              attach
              v-model="selected.statusText"
              :rules="[formRules.required]"
              label="Contract Status *"
              placeholder="Contract Status"></v-select
          ></v-col>
        </v-row>

        <DatePicker
          v-model="selected.renewalDate"
          :rules="[formRules.required]"
          label="Contract Renewal Date *"></DatePicker>

        <v-textarea
        class="my-2"
          dense
          outlined
          v-model="selected.amendments"
          :rows="2"
          label="Contract Amendments"
          placeholder="Additional Notes"></v-textarea>
      </v-form>
    </div>
    <template v-slot:footer>
      <v-card-actions>
        <v-btn text color="error" @click="closeUpdateSlideout()">
          <v-icon left>mdi-chevron-left</v-icon> Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid || slideouts.isLoading || !slideouts.hasChanges"
          @click="updateConfirmed()"
          :loading="slideouts.isLoading">
          <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          {{ selected.id ? 'Save Changes' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import formRules from '@Share/config/form-rules';

  import { $handleError, $isEqual, $formatDate } from '@Utils';
  import dayjs from 'dayjs';
  import { mapGetters } from 'vuex';
  import DatePicker from '@Share/components/VDatePicker';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'edit-contract',
    data() {
      return {
        contractTypes: [
          'Framework Agreement',
          'Service Contract',
          'Purchase Order',
          'Lease Agreement',
        ],
        purchasingOrganizations: ['Org1', 'Org2', 'Org3'],

        documentCurrencies: ['USD', 'EUR', 'GBP', 'JPY'],
        plants: ['Plant1', 'Plant2', 'Plant3'],
        paymentTerms: ['Net 30', 'Net 60', 'Net 90'],
        contractStatuses: ['Active', 'Pending', 'Expired', 'Terminated'],
        valid: false,
        formRules,
        isFetching: false,
        selected: { owner: {} },
        modal: false,
        modal2: false,
        modal3: false,
        modal4: false,
        selectedCemented: { owner: {} },
        slideouts: {
          tab: 0,
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
      };
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapGetters('CONTRACTOR', {
        contractors: 'GET_ENTRIES',
      }),

      ...mapGetters('FACILITY', {
        facilities: 'GET_ENTRIES',
      }),
      editMode() {
        return !!this.selected.id;
      },
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
    },
    mounted() {
      this.$store.dispatch('CONTRACTOR/GET');
      this.$store.dispatch('FACILITY/GET');
    },
    methods: {
      allowedDates(val) {
        return this.selected.validFrom ? dayjs(val).isAfter(this.selected.validFrom) : true;
      },

      onSlideoutClosing() {
        this.$emit('close');
        this.closeUpdateSlideout();
      },
      updateConfirmed() {
        this.slideouts.isLoading = true;
        let toSend = structuredClone(this.selected);
        this.$store
          .dispatch('CONTRACT/UPDATE', toSend)
          .then((res) => {
            this.$emit('onContractUpdate',res);
            this.closeUpdateSlideout();
          })
          .catch((err) => {
            $handleError(err);
          })
          .finally(() => {
            this.slideouts.isLoading = false;
          });
      },
      closeUpdateSlideout() {
        this.selected = { owner: {} };
        this.isFetching = false;
        this.cementContract();
        this.slideouts.active = false;
        this.valid = true;
        this.$refs.editContractForm.resetValidation();
      },
      open(id) {
        if (id != null) {
          this.contractId = id;
          this.fetchContract();
        }
        this.slideouts.active = true;
      },
      $formatDate,
      fetchContract() {
        this.isFetching = true;
        this.$store
          .dispatch('CONTRACT/GET_BY_ID', this.contractId)
          .then((resp) => {
            this.isFetching = false;
            this.slideouts.isLoading = false;
            const data = structuredClone(resp.data);
            data.agreementDate = $formatDate(data.agreementDate, 'YYYY-MM-DD');
            data.validFrom = $formatDate(data.validFrom, 'YYYY-MM-DD');
            data.validTo = $formatDate(data.validTo, 'YYYY-MM-DD');
            data.contractRenewalDate = $formatDate(data.contractRenewalDate, 'YYYY-MM-DD');
            this.selected = data;
            this.cementContract();
          })
          .catch((err) => {
            this.isFetching = false;
            this.closeUpdateSlideout();
            $handleError(err);
          });
      },
      cementContract() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.slideouts.active) return;
        this.slideouts.hasChanges = !$isEqual(this.selected, this.selectedCemented);
      },
      OnResize() {
        let contractSlideout = this.$refs.contractSlideout;
        this.sideOutCurrentSize = contractSlideout.resizeValue;
      },
    },
    watch: {
      contractors() {
        log(this.contractors);
      },
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
      nested: {
        handler() {
          this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
          this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
        },
        deep: true,
      },
    },
    components: {
      DatePicker,
    },
  };
</script>
